import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AgentThread, GetAgentThreadsResponse } from '@dreamai/types';
import api from '../../api';
import { useThreadReads } from '../../providers/ThreadReadsProvider';
import NewProjectModal from './NewProjectModal';
import { ReactComponent as ProjectsFolderIcon } from '../../icons/projects-folder.svg';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import { ReactComponent as ChevronRightIcon } from '../../icons/chevron-right.svg';
import { getProjectTagIconUrl } from '../../utils/projectTagIcon';
import './projects-modal.scss';

interface ProjectSuggestion {
  slug: string;
  title: string;
  image: string;
}

// Fallback suggestions used if the API call fails or is still loading.
const FALLBACK_SUGGESTIONS: ProjectSuggestion[] = [
  { slug: 'plan-a-trip', title: 'Plan a trip', image: 'plan-a-trip.png' },
  {
    slug: 'sort-your-finances',
    title: 'Sort your finances',
    image: 'sort-your-finances.png',
  },
  {
    slug: 'hit-your-fitness-goals',
    title: 'Hit your fitness goals',
    image: 'hit-your-fitness-goals.png',
  },
  {
    slug: 'plan-your-family-life',
    title: 'Plan your family life',
    image: 'plan-your-family-life.png',
  },
  {
    slug: 'organise-your-side-hustle',
    title: 'Organise your side hustle',
    image: 'organise-your-side-hustle.png',
  },
  {
    slug: 'monitor-my-household-bills',
    title: 'Monitor my household bills',
    image: 'monitor-my-house-hold-bills.png',
  },
];

// Subtle gradient tints cycled across suggestion cards (Figma 3949:3327)
const SUGGESTION_TINTS = [
  'radial-gradient(120% 200% at 30% 25%, rgba(72, 159, 217, 0.12) 0%, rgba(255, 254, 252, 0) 70%)',
  'radial-gradient(120% 200% at 30% 25%, rgba(238, 66, 102, 0.1) 0%, rgba(255, 254, 252, 0) 70%)',
  'radial-gradient(120% 200% at 30% 25%, rgba(255, 214, 1, 0.14) 0%, rgba(255, 254, 252, 0) 70%)',
];

interface ProjectsModalProps {
  open: boolean;
  agentId: string | undefined;
  onClose: () => void;
}

/**
 * Centered modal listing the user's active projects alongside suggested
 * starter projects (Figma 3949:3273). Replaces the old sliding
 * ProjectsDrawer.
 */
const ProjectsModal: React.FC<ProjectsModalProps> = ({
  open,
  agentId,
  onClose,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getAllUnreadThreads } = useThreadReads();

  const [projects, setProjects] = useState<AgentThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newModalInitialTitle, setNewModalInitialTitle] = useState<
    string | undefined
  >(undefined);
  const [newModalSuggestionSlug, setNewModalSuggestionSlug] = useState<
    string | undefined
  >(undefined);
  const [suggestions, setSuggestions] =
    useState<ProjectSuggestion[]>(FALLBACK_SUGGESTIONS);

  const unreadThreadIds = useMemo(
    () => new Set(getAllUnreadThreads().map((t) => t.thread_id)),
    [getAllUnreadThreads],
  );

  const fetchSuggestions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        success: boolean;
        suggestions: ProjectSuggestion[];
      }>('/project-suggestions', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success && res.data.suggestions.length > 0) {
        setSuggestions(res.data.suggestions);
      }
    } catch (err) {
      console.error('Failed to load project suggestions:', err);
    }
  }, [getAccessTokenSilently]);

  const fetchProjects = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAgentThreadsResponse>('/agents/threads', {
        params: {
          agent_id: agentId,
          kind: 'Project',
          excludePinned: 'true',
          excludeShared: 'true',
          excludeSubThreads: 'true',
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const threads = res.data.success ? res.data.threads : [];
      setProjects(
        threads
          .filter((t) => !t.is_archived_thread)
          .sort(
            (a, b) =>
              new Date(b.last_updated_at).getTime() -
              new Date(a.last_updated_at).getTime(),
          ),
      );
      setHasFetched(true);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Could not load projects.');
    } finally {
      setLoading(false);
    }
  }, [agentId, getAccessTokenSilently]);

  // Fetch projects + suggestions every time the modal opens
  useEffect(() => {
    if (open) {
      fetchProjects();
      fetchSuggestions();
    }
  }, [open, fetchProjects, fetchSuggestions]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const handleSelect = (threadId: string) => {
    window.location.assign(
      `/conversations?thread_id=${encodeURIComponent(threadId)}`,
    );
  };

  const openNewModal = (title?: string, slug?: string) => {
    setNewModalInitialTitle(title);
    setNewModalSuggestionSlug(slug);
    setShowNewModal(true);
  };

  return (
    <>
      {agentId && (
        <NewProjectModal
          isOpen={showNewModal}
          onClose={() => {
            setShowNewModal(false);
            setNewModalInitialTitle(undefined);
            setNewModalSuggestionSlug(undefined);
          }}
          agentId={agentId}
          initialTitle={newModalInitialTitle}
          suggestionSlug={newModalSuggestionSlug}
          onProjectCreated={() => {
            fetchProjects();
          }}
        />
      )}
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              key='projects-modal-overlay'
              className='projects-modal-overlay'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              onClick={onClose}
            >
              <motion.div
                key='projects-modal'
                className='projects-modal'
                role='dialog'
                aria-modal='true'
                aria-label='Projects'
                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                transition={{
                  type: 'spring',
                  stiffness: 420,
                  damping: 34,
                  mass: 0.6,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <header className='projects-modal-header'>
                  <div className='projects-modal-header-icon'>
                    <ProjectsFolderIcon />
                  </div>
                  <h2 className='projects-modal-title'>Projects</h2>
                  <button
                    type='button'
                    className='projects-modal-new-btn'
                    onClick={() => openNewModal()}
                  >
                    <PlusIcon />
                    <span>New</span>
                  </button>
                </header>

                <div className='projects-modal-body'>
                  {/* ── Active projects ── */}
                  <div className='projects-modal-active'>
                    <div className='projects-modal-section-header'>
                      <span className='projects-modal-section-title'>
                        Active
                      </span>
                      <span className='projects-modal-section-count'>
                        {projects.length}
                      </span>
                    </div>
                    <div className='projects-modal-active-list'>
                      {error && (
                        <div className='projects-modal-message'>{error}</div>
                      )}
                      {!error &&
                        hasFetched &&
                        !loading &&
                        projects.length === 0 && (
                          <div className='projects-modal-message'>
                            No projects yet — start one from the suggestions.
                          </div>
                        )}
                      {projects.map((project) => (
                        <button
                          key={project.id}
                          type='button'
                          className='projects-modal-active-item'
                          onClick={() => handleSelect(project.id)}
                        >
                          <div className='projects-modal-active-item-img'>
                            <img
                              src={
                                getProjectTagIconUrl(project) ||
                                '/splicons/project-tags/other.png'
                              }
                              alt=''
                            />
                          </div>
                          <div className='projects-modal-active-item-text'>
                            <span className='projects-modal-active-item-title'>
                              {project.description || 'Untitled Project'}
                            </span>
                            {unreadThreadIds.has(project.id) && (
                              <span className='projects-modal-active-item-unread'>
                                New updates for you
                              </span>
                            )}
                          </div>
                          <ChevronRightIcon className='projects-modal-chevron' />
                        </button>
                      ))}
                      {loading && (
                        <div className='projects-modal-message'>Loading…</div>
                      )}
                    </div>
                  </div>

                  {/* ── Suggestions ── */}
                  <div className='projects-modal-suggestions'>
                    <div className='projects-modal-section-header'>
                      <span className='projects-modal-section-title'>
                        Suggestions
                      </span>
                    </div>
                    <div className='projects-modal-suggestions-list'>
                      {suggestions.map((suggestion, i) => (
                        <button
                          key={suggestion.slug}
                          type='button'
                          className='projects-modal-suggestion-card'
                          style={
                            {
                              '--suggestion-tint':
                                SUGGESTION_TINTS[i % SUGGESTION_TINTS.length],
                            } as React.CSSProperties
                          }
                          onClick={() =>
                            openNewModal(suggestion.title, suggestion.slug)
                          }
                        >
                          <img
                            src={`/sample-projects/${suggestion.image}`}
                            alt=''
                            className='projects-modal-suggestion-img'
                          />
                          <span className='projects-modal-suggestion-title'>
                            {suggestion.title}
                          </span>
                          <ChevronRightIcon className='projects-modal-chevron' />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default ProjectsModal;
